import React, { useState, useEffect, useRef } from 'react';
import { RiRefreshLine, RiEditLine, RiSaveLine, RiCloseLine } from 'react-icons/ri';
import { API_BASE_URL } from '../config';

const IAAnalysis = ({ slug, data, prompt, currentUser, fetchWithAuth, projectId, startDate, endDate, presetType, presetValue }) => {
  const period = `${presetType}_${presetValue}`;
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedAnalysis, setEditedAnalysis] = useState('');
  const [saving, setSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const fetchingRef = useRef(false);
  const isMountedRef = useRef(true);
  const initialFetchDone = useRef(false);
  const analysisContentRef = useRef(null);

  const isAdmin = currentUser?.isadmin || currentUser?.issuperadmin;
  const isPeriodPassed = new Date() > new Date(endDate);
  const isCustomPeriod = !presetType || 'custom' === presetType;

  // Fonction pour exporter en texte
  const exportAsText = () => {
    const analysisText = analysis?.ba_analysis || analysis?.ia_analysis;
    if (!analysisText) return;

    const timestamp = new Date().toLocaleString('fr-FR');
    const content = `ANALYSE IA - ${slug.toUpperCase()}
=====================================

Période: ${period}
Date de début: ${startDate}
Date de fin: ${endDate}
Date d'export: ${timestamp}

=====================================

${analysisText}

=====================================
Rapport généré par Globe BI - Globe Intelligence
`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `globe-intelligence-${slug}-${period}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Fonction pour exporter en PDF
  const exportAsPDF = async () => {
    const analysisText = analysis?.ba_analysis || analysis?.ia_analysis;
    if (!analysisText) return;

    setIsExporting(true);
    try {
      const { jsPDF } = await import('jspdf');

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const maxLineWidth = pageWidth - 2 * margin;

      let yPosition = margin;

      const addWrappedText = (text, fontSize = 12, isBold = false) => {
        doc.setFontSize(fontSize);
        if (isBold) {
          doc.setFont(undefined, 'bold');
        } else {
          doc.setFont(undefined, 'normal');
        }

        const lines = doc.splitTextToSize(text, maxLineWidth);

        lines.forEach(line => {
          if (yPosition > doc.internal.pageSize.getHeight() - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin, yPosition);
          yPosition += fontSize * 0.5;
        });

        yPosition += 5;
      };

      // Title
      addWrappedText(`GLOBE INTELLIGENCE - ${slug.toUpperCase()}`, 16, true);
      yPosition += 10;

      // Metadata
      addWrappedText(`Période: ${period}`, 10);
      addWrappedText(`Date de début: ${startDate}`, 10);
      addWrappedText(`Date de fin: ${endDate}`, 10);
      addWrappedText(`Date d'export: ${new Date().toLocaleString('fr-FR')}`, 10);
      yPosition += 10;

      // Analysis content
      addWrappedText(analysisText, 11);

      // Footer
      if (yPosition > doc.internal.pageSize.getHeight() - 40) {
        doc.addPage();
        yPosition = margin;
      }

      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text('Rapport généré par Globe BI - Globe Intelligence', margin, doc.internal.pageSize.getHeight() - 15);

      doc.save(`globe-intelligence-${slug}-${period}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
    }
    setIsExporting(false);
  };

  // Fonction pour exporter en image
  const exportAsImage = async () => {
    if (!analysisContentRef.current) return;

    setIsExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;

      const canvas = await html2canvas(analysisContentRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true
      });

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `globe-intelligence-${slug}-${period}-${new Date().toISOString().split('T')[0]}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (error) {
      console.error('Erreur lors de l\'export image:', error);
      alert('Erreur lors de l\'export image. Veuillez réessayer.');
    }
    setIsExporting(false);
  };

  const fetchAnalysis = async (forceRegenerate = false) => {
    if (fetchingRef.current && !forceRegenerate)
      return;

    if (!isPeriodPassed || isCustomPeriod)
      return;

    try {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);

      if (forceRegenerate)
        setRegenerating(true);

      const response = await fetchWithAuth(`${API_BASE_URL}/api/ia/project/${projectId}/analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          slug,
          period,
          data,
          prompt,
          forceRegenerate,
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération de l\'analyse');
      }

      const responseData = await response.json();

      if (isMountedRef.current) {
        setAnalysis(responseData);
        initialFetchDone.current = true;
      }
    } catch (err) {
      console.error('Erreur lors de la récupération de l\'analyse IA:', err);
      if (isMountedRef.current) {
        setError(err.message || 'Une erreur est survenue lors de la récupération de l\'analyse');
        initialFetchDone.current = true;
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setRegenerating(false);
      }
      fetchingRef.current = false;
    }
  };

  const saveBAAnalysis = async () => {
    try {
      setSaving(true);

      const response = await fetchWithAuth(`${API_BASE_URL}/api/ia/project/${projectId}/analysis/ba`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          slug,
          period,
          ba_analysis: editedAnalysis
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la sauvegarde de l\'analyse');
      }

      const responseData = await response.json();

      setAnalysis({
        ...analysis,
        ba_analysis: responseData.ba_analysis,
        ba_edited: true
      });

      setEditing(false);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de l\'analyse:', err);
      setError(err.message || 'Une erreur est survenue lors de la sauvegarde de l\'analyse');
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = () => {
    setEditedAnalysis(analysis.ba_analysis || analysis.ia_analysis);
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditedAnalysis('');
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!initialFetchDone.current && slug && period && data && prompt && projectId && !fetchingRef.current) {
      fetchAnalysis();
    }
  }, [slug, period, data, prompt, projectId]);

  if (isCustomPeriod)
    return null;

  return (
    <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm mt-4">
      <div className="absolute -top-3 left-4">
        <span className="bg-white dark:bg-gray-800 px-2 text-sm font-medium text-blue-500 dark:text-blue-400">
          ✨ Globe Intelligence
        </span>
      </div>

      {!isPeriodPassed && (
        <div className="mt-2">
          <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line text-sm">
            Les analyses sont disponibles uniquement pour les périodes terminées.
          </div>
        </div>
      )}

      {isPeriodPassed && (
        <div className="mt-2">
          <div className="flex justify-between items-center group">
            <div className="flex items-center gap-2">
              {/* Export buttons */}
              {!loading && !editing && analysis && (analysis.ba_analysis || analysis.ia_analysis) && (
                <>
                  <button
                    onClick={exportAsText}
                    disabled={isExporting}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50"
                    title="Exporter en texte"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </button>

                  <button
                    onClick={exportAsPDF}
                    disabled={isExporting}
                    className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50"
                    title="Exporter en PDF"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </button>

                  <button
                    onClick={exportAsImage}
                    disabled={isExporting}
                    className="text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50"
                    title="Exporter en image"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </button>

                  {isExporting && (
                    <div className="flex items-center gap-1">
                      <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-xs text-gray-600 dark:text-gray-400">Export...</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              {isAdmin && !loading && !editing && (
                <>
                  <button
                    onClick={handleEditClick}
                    className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors opacity-0 group-hover:opacity-100 mr-2"
                    title="Éditer l'analyse"
                  >
                    <RiEditLine className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => fetchAnalysis(true)}
                    disabled={regenerating || fetchingRef.current}
                    className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors opacity-0 group-hover:opacity-100"
                    title="Régénérer l'analyse"
                  >
                    <RiRefreshLine className={`w-5 h-5 ${regenerating ? 'animate-spin' : ''}`} />
                  </button>
                </>
              )}
              {isAdmin && editing && (
                <>
                  <button
                    onClick={saveBAAnalysis}
                    disabled={saving}
                    className="text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors mr-2"
                    title="Sauvegarder l'analyse"
                  >
                    <RiSaveLine className={`w-5 h-5 ${saving ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                    title="Annuler"
                  >
                    <RiCloseLine className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
          </div>

          {loading && (
            <div className="py-8 flex justify-center items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}

          {error && !loading && (
            <div className="py-4 text-red-500 dark:text-red-400 text-sm">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && analysis && !editing && (
            <div ref={analysisContentRef}>
              <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line text-sm">
                {analysis.ba_analysis || analysis.ia_analysis}
              </div>
            </div>
          )}

          {editing && (
            <div className="mt-2">
              <textarea
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 min-h-[150px]"
                value={editedAnalysis}
                onChange={(e) => setEditedAnalysis(e.target.value)}
                placeholder="Éditer l'analyse..."
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IAAnalysis;